import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTenant } from '../context/TenantContext';
import { AppContext } from '../context/AppContext';
import { useAuth } from './useAuth';
import { useGovAccess } from './useGovAccess';
import { supabase } from '../services/supabase';
import { canonicalKey } from '../utils/i18nSafeLabel';

const UiLabelsContext = createContext(null);

const labelMapKey = (kind, key) => `${kind}::${key}`;
const orderMapKey = (kind, key, roleCode) => `${kind}::${key}::${roleCode}`;

const DEFAULT_VALUE = {
  canCustomize: false,
  isCustomizeMode: false,
  setIsCustomizeMode: () => {},
  getOverride: () => null,
  resolveLabel: (_kind, _key, fallbackFr, fallbackEn, lang) => (
    lang === 'en' ? (fallbackEn || fallbackFr || '') : (fallbackFr || '')
  ),
  resolveOrder: (_kind, _key, _roleCode, fallbackIndex) => fallbackIndex,
  saveLabel: async () => {},
  saveOrder: async () => {},
};

export const UiLabelsProvider = ({ children }) => {
  const { tenant } = useTenant() || {};
  const appContext = useContext(AppContext);
  const isGlobalEditionMode = appContext?.mode === 'edition';
  const { originalUser, user } = useAuth();
  const gov = useGovAccess(undefined, { actingAs: originalUser }) || {};
  const canAsOriginal = typeof gov.can === 'function' ? gov.can : () => false;
  const isManager = typeof gov.isManager === 'function' ? gov.isManager() : false;
  const isAdmin = typeof gov.isAdmin === 'function' ? gov.isAdmin() : false;
  const sysRoleId = gov.sysRoleId;

  const canCustomize = canAsOriginal('ui_labels', 'write')
    || isManager
    || isAdmin
    || ['dg', 'directeur', 'doyen', 'sec_general', 'secretaire_general', 'recteur', 'admin', 'it_admin', 'master', 'coordonnateur', 'resp_academique'].includes(sysRoleId)
    || Boolean(originalUser?.role && [
      'Master', 'DG', 'Direction', 'Directeur', 'Doyen', 'Recteur', 'Président', 'Secrétaire Général',
      'Administrateur', 'Admin', 'IT', 'Coordination', 'Responsable Académique'
    ].some(r => (originalUser.role || '').toLowerCase().includes(r.toLowerCase())))
    || Boolean(user?.role && [
      'Master', 'DG', 'Direction', 'Directeur', 'Doyen', 'Recteur', 'Président', 'Secrétaire Général',
      'Administrateur', 'Admin', 'IT', 'Coordination', 'Responsable Académique'
    ].some(r => (user.role || '').toLowerCase().includes(r.toLowerCase())));

  const [isCustomizeMode, setIsCustomizeMode] = useState(false);
  const [labels, setLabels] = useState({});
  const [order, setOrder] = useState({});
  const tenantId = tenant?.id || null;

  useEffect(() => {
    let isMounted = true;
    // `channel` reste `null` quand `tenantId` est absent : pas d'abonnement
    // Realtime à nettoyer dans ce cas (voir cleanup plus bas).
    let channel = null;

    // Chargement initial + repli — en fonction async (jamais directement
    // dans le corps de l'effet, y compris la branche `!tenantId`) : un
    // `setState` synchrone au premier niveau d'un effet déclenche des
    // rendus en cascade (react-hooks/set-state-in-effect) ; cette fonction
    // s'exécute immédiatement (aucun `await` avant son premier `return`
    // quand `tenantId` est absent), donc aucun changement de comportement.
    const load = async () => {
      if (!tenantId) {
        if (isMounted) { setLabels({}); setOrder({}); }
        return;
      }
      const [{ data: labelRows }, { data: orderRows }] = await Promise.all([
        supabase.from('tenant_ui_labels').select('*').eq('tenant_id', tenantId),
        supabase.from('tenant_ui_menu_order').select('*').eq('tenant_id', tenantId),
      ]);
      if (!isMounted) return;
      const nextLabels = {};
      (labelRows || []).forEach((row) => { nextLabels[labelMapKey(row.kind, row.label_key)] = row; });
      const nextOrder = {};
      (orderRows || []).forEach((row) => { nextOrder[orderMapKey(row.kind, row.label_key, row.role_code)] = row.sort_order; });
      setLabels(nextLabels);
      setOrder(nextOrder);
    };
    load();

    if (tenantId) {
      channel = supabase
        .channel(`tenant_ui_labels_${tenantId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tenant_ui_labels', filter: `tenant_id=eq.${tenantId}` }, (payload) => {
          setLabels((prev) => {
            const next = { ...prev };
            const row = payload.new || payload.old;
            const k = labelMapKey(row.kind, row.label_key);
            if (payload.eventType === 'DELETE') delete next[k];
            else next[k] = payload.new;
            return next;
          });
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'tenant_ui_menu_order', filter: `tenant_id=eq.${tenantId}` }, (payload) => {
          setOrder((prev) => {
            const next = { ...prev };
            const row = payload.new || payload.old;
            const k = orderMapKey(row.kind, row.label_key, row.role_code);
            if (payload.eventType === 'DELETE') delete next[k];
            else next[k] = payload.new.sort_order;
            return next;
          });
        })
        .subscribe();
    }

    return () => {
      isMounted = false;
      if (channel) supabase.removeChannel(channel);
    };
  }, [tenantId]);

  const getOverride = useCallback((kind, key, lang) => {
    if (!key) return null;
    const baseKey = canonicalKey(key);

    // 1. Correspondance directe
    let row = labels[labelMapKey(kind, key)];

    // 2. Correspondance canonique (sans suffixe de portail -piloter, etc.)
    if (!row && baseKey !== key) {
      row = labels[labelMapKey(kind, baseKey)];
    }

    // 3. Si la clé demandée est canonique, chercher parmi les variantes de portail existantes
    if (!row && baseKey === key) {
      const suffixes = ['-piloter', '-produire', '-soutenir', '-controler', '-ameliorer'];
      for (const sfx of suffixes) {
        const candidate = labels[labelMapKey(kind, `${key}${sfx}`)];
        if (candidate) {
          row = candidate;
          break;
        }
      }
    }

    // 4. Repli croisé page_title <-> menu_item
    if (!row) {
      if (kind === 'page_title') {
        row = labels[labelMapKey('menu_item', key)] || labels[labelMapKey('menu_item', baseKey)];
      } else if (kind === 'menu_item') {
        row = labels[labelMapKey('page_title', key)] || labels[labelMapKey('page_title', baseKey)];
      }
    }

    // 5. Repli pour les sous-groupes de menu
    if (!row && (key.startsWith('group::') || kind === 'menu_group')) {
      const gKey = key.startsWith('group::') ? key : `group::${key}`;
      row = labels[labelMapKey('menu_section', gKey)];
    }

    const value = lang === 'en' ? row?.label_en : row?.label_fr;
    return value && value.trim() ? value : null;
  }, [labels]);

  const resolveLabel = useCallback((kind, key, fallbackFr, fallbackEn, lang) => {
    const override = getOverride(kind, key, lang);
    if (override) return override;
    return lang === 'en' ? (fallbackEn || fallbackFr || '') : (fallbackFr || '');
  }, [getOverride]);

  const resolveOrder = useCallback((kind, key, roleCode, fallbackIndex) => {
    if (!key) return fallbackIndex;
    const baseKey = canonicalKey(key);

    // 1. Override spécifique au rôle
    let value = roleCode ? order[orderMapKey(kind, key, roleCode)] : undefined;
    if (typeof value !== 'number' && baseKey !== key && roleCode) {
      value = order[orderMapKey(kind, baseKey, roleCode)];
    }

    // 2. Override universel (*) pour tous les utilisateurs du tenant
    if (typeof value !== 'number') {
      value = order[orderMapKey(kind, key, '*')]
        ?? order[orderMapKey(kind, baseKey, '*')]
        ?? order[orderMapKey(kind, key, 'all')]
        ?? order[orderMapKey(kind, baseKey, 'all')]
        ?? order[orderMapKey(kind, key, 'default')]
        ?? order[orderMapKey(kind, baseKey, 'default')];
    }

    // 3. Repli si un ordre existe pour n'importe quel rôle sur cet élément
    if (typeof value !== 'number') {
      const prefix1 = `${kind}::${key}::`;
      const prefix2 = baseKey !== key ? `${kind}::${baseKey}::` : null;
      for (const k in order) {
        if (k.startsWith(prefix1) || (prefix2 && k.startsWith(prefix2))) {
          value = order[k];
          break;
        }
      }
    }

    return typeof value === 'number' ? value : fallbackIndex;
  }, [order]);

  const saveLabel = useCallback(async (kind, key, lang, value) => {
    if (!tenantId || !key) return;
    const column = lang === 'en' ? 'label_en' : 'label_fr';
    const baseKey = canonicalKey(key);

    const rows = [{
      tenant_id: tenantId,
      label_key: key,
      kind,
      [column]: value,
      updated_by: originalUser?.id,
    }];

    // Si la clé porte un suffixe de portail (ex: identite-institutionnelle-piloter),
    // enregistrer également la clé canonique (identite-institutionnelle) pour
    // répercuter instantanément le nouveau nom à tous les portails et sections !
    if (baseKey !== key) {
      rows.push({
        tenant_id: tenantId,
        label_key: baseKey,
        kind,
        [column]: value,
        updated_by: originalUser?.id,
      });
    }

    const { error } = await supabase.from('tenant_ui_labels').upsert(rows, { onConflict: 'tenant_id,label_key,kind' });
    if (error) throw error;
  }, [tenantId, originalUser]);

  const saveOrder = useCallback(async (kind, roleCode, entries) => {
    if (!tenantId) return;
    const baseRows = entries.map(({ key, sortOrder }) => ({
      tenant_id: tenantId,
      role_code: roleCode || '*',
      label_key: key,
      kind,
      sort_order: sortOrder,
      updated_by: originalUser?.id,
    }));

    // Sauvegarde également sous le rôle universel '*' pour que la réorganisation
    // se répercute instantanément sur tous les utilisateurs du tenant
    const universalRows = (roleCode && roleCode !== '*')
      ? entries.map(({ key, sortOrder }) => ({
          tenant_id: tenantId,
          role_code: '*',
          label_key: key,
          kind,
          sort_order: sortOrder,
          updated_by: originalUser?.id,
        }))
      : [];

    const { error } = await supabase.from('tenant_ui_menu_order').upsert([...baseRows, ...universalRows], {
      onConflict: 'tenant_id,role_code,label_key,kind'
    });
    if (error) throw error;
  }, [tenantId, originalUser]);

  // Le mode public est toujours borné par le droit réel : un état brut resté
  // à `true` (session prolongée dans l'onglet, capacité retirée en cours de
  // route) ne doit jamais exposer crayons ni glisser-déposer.
  // Activé soit via la bascule dédiée (isCustomizeMode), soit en mode Configuration/Édition global.
  const effectiveCustomizeMode = canCustomize && (isCustomizeMode || isGlobalEditionMode);

  const value = useMemo(() => ({
    canCustomize, isCustomizeMode: effectiveCustomizeMode, setIsCustomizeMode,
    getOverride, resolveLabel, resolveOrder, saveLabel, saveOrder,
  }), [canCustomize, effectiveCustomizeMode, getOverride, resolveLabel, resolveOrder, saveLabel, saveOrder]);

  return <UiLabelsContext.Provider value={value}>{children}</UiLabelsContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useUiLabels = () => useContext(UiLabelsContext) || DEFAULT_VALUE;
